/* eslint-disable react/no-unstable-nested-components */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import PageLayout from "@/components/PageLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Filter, UserX, Wallet, ExternalLink, Send, CheckCircle2, Trash2, Phone, Clock, Bell, BellOff, PhoneCall, Printer } from "lucide-react";
import { useYear } from "@/contexts/YearContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Stati che rientrano nel filtro "Inconcludenti"
const STATI_INCONCLUDENTI = [
  "Non Risposto da Richiamare",
  "Non Risposto 2° Tentativo",
  "Non Risposto 3° Tentativo",
  "In Attesa Riscontri Cliente",
  "Sparito",
  "Perditempo"
];

export default function FunnelRipresaPage({ user, logout }) {
  const { selectedYear } = useYear();
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Tab attiva (Follow UP di default per priorità utente)
  const [activeTab, setActiveTab] = useState("follow_up");
  
  // Filtri marketing per sezione Inconcludenti
  const [filtroInconcludenti, setFiltroInconcludenti] = useState("all");
  // Filtri marketing per sezione Fuori Budget
  const [filtroFuoriBudget, setFiltroFuoriBudget] = useState("all");
  // Filtro stato funnel: 'all' | 'to_send' | 'sent'
  const [filtroFunnelStato, setFiltroFunnelStato] = useState("all");
  
  // Permesso notifiche browser
  const [notifPermission, setNotifPermission] = useState(
    typeof window !== "undefined" && "Notification" in window ? Notification.permission : "unsupported"
  );
  
  const requestNotifPermission = async () => {
    if (!("Notification" in window)) {
      toast.error("Il browser non supporta le notifiche desktop");
      return;
    }
    try {
      const res = await Notification.requestPermission();
      setNotifPermission(res);
      if (res === "granted") {
        toast.success("Notifiche attivate — sarai avvisato anche da altre tab");
        // Notifica di conferma
        try {
          new Notification("Notifiche attivate", {
            body: "Riceverai un avviso desktop quando un memo scade",
            icon: "/favicon.ico",
            tag: "notif-confirm",
          });
        } catch { /* noop */ }
      } else if (res === "denied") {
        toast.error("Notifiche bloccate. Abilitarle dalle impostazioni del browser.");
      }
    } catch (e) {
      console.warn("Notification permission request failed:", e);
    }
  };
  
  // Impostazioni suoneria (soft/normal/urgent)
  const [alarmTone, setAlarmTone] = useState("normal");
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/settings`, { withCredentials: true });
        if (res.data?.memo_alarm_tone) setAlarmTone(res.data.memo_alarm_tone);
      } catch { /* noop */ }
    })();
  }, []);
  
  // Allarme acustico + notifiche browser memo scaduti (ogni 15 min per 3 sec)
  useEffect(() => {
    const beep = () => {
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (!AudioCtx) return;
        const ctx = new AudioCtx();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        // Configurazione tono in base a alarmTone
        const config = {
          soft:   { freq: 523, duration: 1.5, maxGain: 0.15, pulseInterval: 0.5 },
          normal: { freq: 880, duration: 3.0, maxGain: 0.30, pulseInterval: 0.6 },
          urgent: { freq: 1200, duration: 5.0, maxGain: 0.45, pulseInterval: 0.3 },
        }[alarmTone] || { freq: 880, duration: 3.0, maxGain: 0.30, pulseInterval: 0.6 };
        osc.type = "sine";
        osc.frequency.value = config.freq;
        gain.gain.setValueAtTime(0.001, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(config.maxGain, ctx.currentTime + 0.05);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start();
        const start = ctx.currentTime;
        for (let t = config.pulseInterval; t < config.duration; t += 2 * config.pulseInterval) {
          gain.gain.setValueAtTime(0.001, start + t);
          gain.gain.setValueAtTime(config.maxGain, start + t + config.pulseInterval);
        }
        setTimeout(() => {
          try { osc.stop(); ctx.close(); } catch { /* noop */ }
        }, (config.duration + 0.2) * 1000);
      } catch (e) {
        console.warn("Beep failed:", e);
      }
    };
    
    const sendNotification = (scadutiList) => {
      if (typeof window === "undefined" || !("Notification" in window)) return;
      if (Notification.permission !== "granted") return;
      try {
        const count = scadutiList.length;
        const title = count === 1
          ? `Memo scaduto: ${scadutiList[0].nome} ${scadutiList[0].cognome}`
          : `${count} memo scaduti in Follow UP`;
        const body = count === 1
          ? (scadutiList[0].memo_nota || "Contattare il cliente")
          : scadutiList.slice(0, 3).map(s => `• ${s.nome} ${s.cognome}`).join("\n");
        const n = new Notification(title, {
          body,
          icon: "/favicon.ico",
          tag: "memo-alarm",  // sostituisce le notifiche precedenti dello stesso tag
          requireInteraction: true,
        });
        n.onclick = () => {
          window.focus();
          n.close();
        };
      } catch (e) {
        console.warn("Notification failed:", e);
      }
    };
    
    let lastBeep = 0;
    const check = () => {
      const now = Date.now();
      const scadutiList = interviews.filter((i) => {
        if (i.stato !== "Consegnato") return false;
        if (i.memo_muted) return false;
        if (!i.memo_datetime) return false;
        try { return new Date(i.memo_datetime).getTime() <= now; } catch { return false; }
      });
      const hasActiveAlarm = scadutiList.length > 0;
      if (hasActiveAlarm && (now - lastBeep) >= 15 * 60 * 1000) {
        lastBeep = now;
        // Notifica desktop sempre (funziona anche in tab non attive)
        sendNotification(scadutiList);
        // Beep solo se sono sulla tab Follow UP
        if (activeTab === "follow_up") beep();
      }
    };
    const initialTimer = setTimeout(check, 3000);
    const interval = setInterval(check, 60 * 1000);
    return () => { clearTimeout(initialTimer); clearInterval(interval); };
  }, [interviews, activeTab, alarmTone]);

  const fetchInterviews = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/interviews?year=${selectedYear}`, { withCredentials: true });
      setInterviews(res.data);
    } catch (e) {
      console.error("Error fetching interviews:", e);
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInterviews();
  }, [selectedYear]);

  // Filtra clienti inconcludenti
  const clientiInconcludenti = interviews.filter(i => STATI_INCONCLUDENTI.includes(i.stato));
  
  // Filtra clienti fuori budget
  const clientiFuoriBudget = interviews.filter(i => i.stato === "Fuori Budget");

  // Follow UP: clienti con stato "Consegnato" (offerta consegnata in intervista)
  const clientiFollowUp = interviews
    .filter(i => i.stato === "Consegnato")
    .sort((a, b) => {
      // Ordina per data_consegnato desc (più recenti prima)
      const da = a.data_consegnato ? new Date(a.data_consegnato).getTime() : 0;
      const db = b.data_consegnato ? new Date(b.data_consegnato).getTime() : 0;
      return db - da;
    });
  
  // Filtri Follow UP: ricerca per nome + filtro per stato memo + ordinamento
  // Persistiti in localStorage per ripristino automatico al prossimo accesso
  const [followUpSearch, setFollowUpSearch] = useState(() => {
    try { return localStorage.getItem("fu_search") || ""; } catch { return ""; }
  });
  const [followUpMemoFilter, setFollowUpMemoFilter] = useState(() => {
    try { return localStorage.getItem("fu_memo_filter") || "all"; } catch { return "all"; }
  }); // all | scaduto | oggi | futuro | senza
  const [followUpSort, setFollowUpSort] = useState(() => {
    try { return localStorage.getItem("fu_sort") || "memo_asc"; } catch { return "memo_asc"; }
  }); // memo_asc | memo_desc | giorni_asc | giorni_desc
  
  useEffect(() => { try { localStorage.setItem("fu_search", followUpSearch); } catch { /* noop */ } }, [followUpSearch]);
  useEffect(() => { try { localStorage.setItem("fu_memo_filter", followUpMemoFilter); } catch { /* noop */ } }, [followUpMemoFilter]);
  useEffect(() => { try { localStorage.setItem("fu_sort", followUpSort); } catch { /* noop */ } }, [followUpSort]);
  
  // Genera e stampa la lista Follow UP filtrata in una nuova finestra
  const handlePrintFollowUp = () => {
    const rows = clientiFollowUpFiltered;
    if (rows.length === 0) {
      toast.info("Nessun cliente da stampare con questi filtri");
      return;
    }
    const now = new Date();
    const dateStr = now.toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
    const timeStr = now.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
    const escapeHtml = (s) => String(s || "").replace(/[&<>"']/g, (c) => (
      { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
    ));
    const filterLabel = { all: "Tutti", scaduto: "Scaduti", oggi: "Oggi", futuro: "Futuri", senza: "Senza memo" }[followUpMemoFilter] || "";
    const sortLabel = { memo_asc: "Data Memo ↑", memo_desc: "Data Memo ↓", giorni_asc: "Giorni ↑", giorni_desc: "Giorni ↓" }[followUpSort] || "";
    const rowsHtml = rows.map((c, idx) => {
      const giorni = giorniDaConsegnato(c.data_consegnato);
      const memo = c.memo_datetime ? new Date(c.memo_datetime).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
      const chiamate = Array.isArray(c.chiamate_log) ? c.chiamate_log : [];
      const chiamateHtml = chiamate.length === 0
        ? '<em style="color:#94a3b8">Nessuna chiamata</em>'
        : chiamate.map(ch => `<div style="margin-bottom:4px"><strong style="font-size:10px;color:#64748b">${escapeHtml(formatChiamataDate(ch.data))}</strong><br>${escapeHtml(ch.nota || "(vuota)")}</div>`).join("");
      const scaduto = c.memo_datetime && new Date(c.memo_datetime).getTime() <= Date.now();
      const rowStyle = scaduto ? 'background:#fef2f2' : (idx % 2 === 0 ? 'background:#f8fafc' : '');
      return `
        <tr style="${rowStyle}">
          <td style="padding:8px;border-bottom:1px solid #e2e8f0;font-weight:600">${escapeHtml(c.nome)} ${escapeHtml(c.cognome)}</td>
          <td style="padding:8px;border-bottom:1px solid #e2e8f0">${escapeHtml(c.telefono || "—")}</td>
          <td style="padding:8px;border-bottom:1px solid #e2e8f0;text-align:center">${giorni}</td>
          <td style="padding:8px;border-bottom:1px solid #e2e8f0;font-size:11px">${chiamateHtml}</td>
          <td style="padding:8px;border-bottom:1px solid #e2e8f0;${scaduto ? 'color:#dc2626;font-weight:600' : ''}">${escapeHtml(memo)}${scaduto ? ' ⚠️' : ''}</td>
          <td style="padding:8px;border-bottom:1px solid #e2e8f0;font-size:11px">${escapeHtml(c.memo_nota || "")}</td>
          <td style="padding:8px;border-bottom:1px solid #e2e8f0;width:60px"></td>
        </tr>`;
    }).join("");
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Lista Follow UP — ${dateStr}</title>
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; margin:24px; color:#0f172a; }
  h1 { margin:0 0 4px; font-size:20px; }
  .meta { color:#64748b; font-size:12px; margin-bottom:16px; }
  table { width:100%; border-collapse:collapse; font-size:12px; }
  th { text-align:left; padding:8px; background:#1e40af; color:#fff; font-size:11px; text-transform:uppercase; letter-spacing:0.05em; }
  th:last-child { text-align:center; }
  @media print {
    body { margin:12px; }
    tr { page-break-inside: avoid; }
    .no-print { display:none; }
  }
  .print-btn { background:#1e40af; color:#fff; border:none; padding:8px 16px; border-radius:4px; cursor:pointer; margin-bottom:12px; font-size:14px; }
</style></head><body>
<button class="no-print print-btn" onclick="window.print()">🖨️ Stampa</button>
<h1>Lista Follow UP — Piano richiami</h1>
<div class="meta">Generato il ${dateStr} alle ${timeStr} • ${rows.length} client${rows.length === 1 ? 'e' : 'i'} • Filtro: ${filterLabel} • Ordinamento: ${sortLabel}${followUpSearch ? ` • Ricerca: "${escapeHtml(followUpSearch)}"` : ""}</div>
<table>
  <thead><tr>
    <th>Nominativo</th>
    <th>Telefono</th>
    <th>Giorni</th>
    <th>Chiamate</th>
    <th>Data Memo</th>
    <th>Nota Memo</th>
    <th>Esito oggi</th>
  </tr></thead>
  <tbody>${rowsHtml}</tbody>
</table>
</body></html>`;
    const w = window.open("", "_blank", "width=1000,height=800");
    if (!w) { toast.error("Impossibile aprire la finestra di stampa (popup bloccato?)"); return; }
    w.document.open();
    w.document.write(html);
    w.document.close();
    setTimeout(() => { try { w.focus(); w.print(); } catch { /* noop */ } }, 400);
  };
  
  const clientiFollowUpFiltered = clientiFollowUp
    .filter((c) => {
    // Search
    const q = followUpSearch.trim().toLowerCase();
    if (q) {
      const hay = `${c.nome || ""} ${c.cognome || ""} ${c.telefono || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    // Filtro memo
    if (followUpMemoFilter === "all") return true;
    if (followUpMemoFilter === "senza") return !c.memo_datetime;
    if (!c.memo_datetime) return false;
    let dt;
    try { dt = new Date(c.memo_datetime); } catch { return false; }
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const endOfToday = startOfToday + 24 * 60 * 60 * 1000;
    const ts = dt.getTime();
    if (followUpMemoFilter === "scaduto") return ts <= now.getTime();
    if (followUpMemoFilter === "oggi") return ts >= startOfToday && ts < endOfToday && ts > now.getTime();
    if (followUpMemoFilter === "futuro") return ts >= endOfToday;
    return true;
  })
    .sort((a, b) => {
      const memoA = a.memo_datetime ? new Date(a.memo_datetime).getTime() : Number.POSITIVE_INFINITY;
      const memoB = b.memo_datetime ? new Date(b.memo_datetime).getTime() : Number.POSITIVE_INFINITY;
      const giorniA = a.data_consegnato ? Math.floor((Date.now() - new Date(a.data_consegnato).getTime()) / 86400000) : -1;
      const giorniB = b.data_consegnato ? Math.floor((Date.now() - new Date(b.data_consegnato).getTime()) / 86400000) : -1;
      switch (followUpSort) {
        case "memo_asc": return memoA - memoB;
        case "memo_desc": return memoB - memoA;
        case "giorni_asc": return giorniA - giorniB;
        case "giorni_desc": return giorniB - giorniA;
        default: return 0;
      }
    });
  
  const followUpCounts = {
    all: clientiFollowUp.length,
    scaduto: clientiFollowUp.filter((c) => {
      if (!c.memo_datetime) return false;
      try { return new Date(c.memo_datetime).getTime() <= Date.now(); } catch { return false; }
    }).length,
    oggi: clientiFollowUp.filter((c) => {
      if (!c.memo_datetime) return false;
      try {
        const t = new Date(c.memo_datetime).getTime();
        const now = new Date();
        const s = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
        return t >= s && t < s + 86400000 && t > now.getTime();
      } catch { return false; }
    }).length,
    futuro: clientiFollowUp.filter((c) => {
      if (!c.memo_datetime) return false;
      try {
        const t = new Date(c.memo_datetime).getTime();
        const now = new Date();
        const s = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
        return t >= s + 86400000;
      } catch { return false; }
    }).length,
    senza: clientiFollowUp.filter((c) => !c.memo_datetime).length,
  };

  // Applica filtri marketing alla sezione Inconcludenti
  const inconcludentiFiltrati = clientiInconcludenti.filter(i => {
    if (filtroInconcludenti === "all") return true;
    const cat = i.categoria_merceologica || "";
    if (filtroInconcludenti === "solo_porte") return cat === "Porte";
    if (filtroInconcludenti === "porte_e_infissi") return cat === "Infissi e Porte";
    if (filtroInconcludenti === "solo_infissi") return cat === "Infissi";
    return true;
  });

  // Applica filtri marketing alla sezione Fuori Budget
  const fuoriBudgetFiltrati = clientiFuoriBudget.filter(i => {
    if (filtroFuoriBudget === "all") return true;
    const cat = i.categoria_merceologica || "";
    if (filtroFuoriBudget === "solo_porte") return cat === "Porte";
    if (filtroFuoriBudget === "porte_e_infissi") return cat === "Infissi e Porte";
    if (filtroFuoriBudget === "solo_infissi") return cat === "Infissi";
    return true;
  });

  // Conteggi per filtri Inconcludenti
  const countInconcludenti = {
    all: clientiInconcludenti.length,
    solo_porte: clientiInconcludenti.filter(i => i.categoria_merceologica === "Porte").length,
    porte_e_infissi: clientiInconcludenti.filter(i => i.categoria_merceologica === "Infissi e Porte").length,
    solo_infissi: clientiInconcludenti.filter(i => i.categoria_merceologica === "Infissi").length
  };

  // Conteggi per filtri Fuori Budget
  const countFuoriBudget = {
    all: clientiFuoriBudget.length,
    solo_porte: clientiFuoriBudget.filter(i => i.categoria_merceologica === "Porte").length,
    porte_e_infissi: clientiFuoriBudget.filter(i => i.categoria_merceologica === "Infissi e Porte").length,
    solo_infissi: clientiFuoriBudget.filter(i => i.categoria_merceologica === "Infissi").length
  };

  const handleAddToFunnel = async (interviewId, funnelNumber, clienteNome) => {
    if (funnelNumber !== 1) {
      toast.info(`Funnel ${funnelNumber} non ancora implementato`);
      return;
    }
    const toastId = toast.loading(`Spostamento di ${clienteNome} su Brevo in corso…`);
    try {
      const res = await axios.post(
        `${API}/funnel/brevo/move-to-ripresa`,
        { interview_id: interviewId },
        { withCredentials: true }
      );
      if (res.data?.success) {
        toast.success(
          `${clienteNome} spostato in "Contatti in Ripresa" su Brevo${res.data.phone_synced ? "" : " (telefono non sincronizzato)"}`,
          { id: toastId }
        );
        // Aggiorna localmente l'intervista per riflettere lo stato "inviato"
        setInterviews(prev => prev.map(i =>
          i.id === interviewId
            ? { ...i, funnel_brevo: { ...(i.funnel_brevo || {}), funnel_1: { sent_at: res.data.sent_at, list_id: res.data.moved_to_list } } }
            : i
        ));
      } else {
        toast.error("Operazione fallita", { id: toastId });
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Errore di rete";
      toast.error(`Errore Brevo: ${msg}`, { id: toastId });
    }
  };

  const handleDeleteCliente = async (cliente) => {
    const nomeCompleto = `${cliente.nome || ''} ${cliente.cognome || ''}`.trim() || cliente.email || "questo contatto";
    const conferma1 = window.confirm(
      `⚠️ ATTENZIONE: stai per eliminare DEFINITIVAMENTE "${nomeCompleto}".\n\n` +
      `Verranno cancellati per sempre:\n` +
      `• L'intervista\n` +
      `• Eventuali offerte collegate (e relativi documenti caricati)\n` +
      `• Eventuali pagamenti e contabili\n` +
      `• Eventuali commesse di posa e foto\n` +
      `• Follow-up, rilievi manuali, ordini collegati\n` +
      `• La pagina su Notion\n\n` +
      `Questa azione è IRREVERSIBILE. Sei sicuro?`
    );
    if (!conferma1) return;
    const conferma2 = window.prompt(
      `Per confermare, scrivi esattamente:\n\nELIMINA\n\nse vuoi davvero cancellare "${nomeCompleto}" e tutti i suoi dati.`
    );
    if (conferma2 !== "ELIMINA") {
      toast.info("Eliminazione annullata");
      return;
    }
    const toastId = toast.loading(`Eliminazione di ${nomeCompleto} in corso…`);
    try {
      const res = await axios.delete(
        `${API}/interviews/${cliente.id}/cascade`,
        { withCredentials: true }
      );
      const d = res.data?.deleted || {};
      toast.success(
        `${nomeCompleto} eliminato. Cancellati: ${d.offers||0} offerte, ${d.payments||0} pagamenti, ${d.commesse_pose||0} commesse, ${d.cloud_files||0} file cloud.`,
        { id: toastId, duration: 6000 }
      );
      // Rimuovi dalla lista locale
      setInterviews(prev => prev.filter(i => i.id !== cliente.id));
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Errore di rete";
      toast.error(`Errore: ${msg}`, { id: toastId });
    }
  };

  // Aggiorna un campo Follow UP (memo_datetime) via PUT /interviews
  const handleUpdateFollowUp = async (interviewId, field, value) => {
    try {
      await axios.put(
        `${API}/interviews/${interviewId}`,
        { [field]: value },
        { withCredentials: true }
      );
      setInterviews(prev => prev.map(i => i.id === interviewId ? { ...i, [field]: value } : i));
      toast.success("Salvato", { duration: 1500 });
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Errore di rete";
      toast.error(`Errore salvataggio: ${msg}`);
    }
  };

  // Aggiunge una chiamata al log con timestamp automatico
  const handleAddChiamata = async (interviewId, nota) => {
    try {
      const res = await axios.post(
        `${API}/interviews/${interviewId}/chiamate`,
        { nota },
        { withCredentials: true }
      );
      setInterviews(prev => prev.map(i => i.id === interviewId ? { ...i, chiamate_log: res.data.chiamate_log || [] } : i));
      toast.success("Chiamata registrata", { duration: 1500 });
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Errore di rete";
      toast.error(`Errore: ${msg}`);
    }
  };

  // Aggiorna la nota di una chiamata esistente
  const handleUpdateChiamataNota = async (interviewId, index, nota) => {
    try {
      const res = await axios.put(
        `${API}/interviews/${interviewId}/chiamate/${index}`,
        { nota },
        { withCredentials: true }
      );
      setInterviews(prev => prev.map(i => i.id === interviewId ? { ...i, chiamate_log: res.data.chiamate_log || [] } : i));
      toast.success("Nota aggiornata", { duration: 1500 });
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Errore di rete";
      toast.error(`Errore: ${msg}`);
    }
  };

  // Elimina una chiamata dal log
  const handleDeleteChiamata = async (interviewId, index) => {
    if (!window.confirm("Eliminare questa chiamata dal registro?")) return;
    try {
      const res = await axios.delete(
        `${API}/interviews/${interviewId}/chiamate/${index}`,
        { withCredentials: true }
      );
      setInterviews(prev => prev.map(i => i.id === interviewId ? { ...i, chiamate_log: res.data.chiamate_log || [] } : i));
      toast.success("Chiamata eliminata", { duration: 1500 });
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Errore di rete";
      toast.error(`Errore: ${msg}`);
    }
  };

  // Formatta timestamp ISO in "DD/MM HH:mm"
  const formatChiamataDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch { return ""; }
  };

  // Calcola giorni trascorsi da data_consegnato
  const giorniDaConsegnato = (isoDate) => {
    if (!isoDate) return "—";
    try {
      const consegnato = new Date(isoDate);
      const now = new Date();
      const diff = Math.floor((now - consegnato) / (1000 * 60 * 60 * 24));
      return diff >= 0 ? diff : 0;
    } catch { return "—"; }
  };

  // True se la data memo è nel passato (allerta scaduta)
  const memoScaduto = (memoIso) => {
    if (!memoIso) return false;
    try {
      return new Date(memoIso).getTime() <= Date.now();
    } catch { return false; }
  };

  // Formatta un ISO datetime per l'input type=datetime-local (YYYY-MM-DDTHH:mm)
  const isoToLocalInput = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch { return ""; }
  };

  const FiltriMarketing = ({ filtro, setFiltro, counts }) => (
    <div className="flex flex-wrap gap-2 mb-4">
      <Button
        variant={filtro === "all" ? "default" : "outline"}
        size="sm"
        onClick={() => setFiltro("all")}
        className={filtro === "all" ? "bg-purple-600 hover:bg-purple-700" : "hover:bg-purple-50 border-purple-200"}
      >
        Tutti
        <span className={`ml-2 px-1.5 py-0.5 rounded-full text-xs ${
          filtro === "all" ? "bg-white/20 text-white" : "bg-purple-100 text-purple-700"
        }`}>
          {counts.all}
        </span>
      </Button>
      <Button
        variant={filtro === "solo_porte" ? "default" : "outline"}
        size="sm"
        onClick={() => setFiltro("solo_porte")}
        className={filtro === "solo_porte" ? "bg-orange-500 hover:bg-orange-600" : "hover:bg-orange-50 border-orange-200 text-orange-700"}
      >
        Solo Porte
        <span className={`ml-2 px-1.5 py-0.5 rounded-full text-xs ${
          filtro === "solo_porte" ? "bg-white/20 text-white" : "bg-orange-100 text-orange-700"
        }`}>
          {counts.solo_porte}
        </span>
      </Button>
      <Button
        variant={filtro === "porte_e_infissi" ? "default" : "outline"}
        size="sm"
        onClick={() => setFiltro("porte_e_infissi")}
        className={filtro === "porte_e_infissi" ? "bg-teal-500 hover:bg-teal-600" : "hover:bg-teal-50 border-teal-200 text-teal-700"}
      >
        Porte e Infissi
        <span className={`ml-2 px-1.5 py-0.5 rounded-full text-xs ${
          filtro === "porte_e_infissi" ? "bg-white/20 text-white" : "bg-teal-100 text-teal-700"
        }`}>
          {counts.porte_e_infissi}
        </span>
      </Button>
      <Button
        variant={filtro === "solo_infissi" ? "default" : "outline"}
        size="sm"
        onClick={() => setFiltro("solo_infissi")}
        className={filtro === "solo_infissi" ? "bg-sky-500 hover:bg-sky-600" : "hover:bg-sky-50 border-sky-200 text-sky-700"}
      >
        Solo Infissi
        <span className={`ml-2 px-1.5 py-0.5 rounded-full text-xs ${
          filtro === "solo_infissi" ? "bg-white/20 text-white" : "bg-sky-100 text-sky-700"
        }`}>
          {counts.solo_infissi}
        </span>
      </Button>
    </div>
  );

  // Helper: formatta data "DD/MM" da ISO timestamp
  const formatSentDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}`;
    } catch { return ""; }
  };

  // Helper: applica filtro stato funnel su una lista
  const applyFunnelFilter = (lista) => {
    if (filtroFunnelStato === "all") return lista;
    if (filtroFunnelStato === "sent") return lista.filter(i => i.funnel_brevo?.funnel_1?.sent_at);
    if (filtroFunnelStato === "to_send") return lista.filter(i => !i.funnel_brevo?.funnel_1?.sent_at);
    return lista;
  };

  // Filtri stato funnel
  const FiltriStatoFunnel = ({ lista }) => {
    const counts = {
      all: lista.length,
      to_send: lista.filter(i => !i.funnel_brevo?.funnel_1?.sent_at).length,
      sent: lista.filter(i => i.funnel_brevo?.funnel_1?.sent_at).length,
    };
    const buttons = [
      {
        key: "all", label: "Tutti",
        activeCls: "bg-slate-600 hover:bg-slate-700 text-white",
        idleCls: "hover:bg-slate-50 border-slate-200 text-slate-700",
        badgeActive: "bg-white/20 text-white",
        badgeIdle: "bg-slate-100 text-slate-700",
      },
      {
        key: "to_send", label: "Da inviare",
        activeCls: "bg-blue-600 hover:bg-blue-700 text-white",
        idleCls: "hover:bg-blue-50 border-blue-200 text-blue-700",
        badgeActive: "bg-white/20 text-white",
        badgeIdle: "bg-blue-100 text-blue-700",
      },
      {
        key: "sent", label: "Già inviati",
        activeCls: "bg-emerald-600 hover:bg-emerald-700 text-white",
        idleCls: "hover:bg-emerald-50 border-emerald-200 text-emerald-700",
        badgeActive: "bg-white/20 text-white",
        badgeIdle: "bg-emerald-100 text-emerald-700",
      },
    ];
    return (
      <div className="flex flex-wrap gap-2 mb-3" data-testid="filtri-stato-funnel">
        {buttons.map(b => {
          const active = filtroFunnelStato === b.key;
          return (
            <Button
              key={b.key}
              variant={active ? "default" : "outline"}
              size="sm"
              onClick={() => setFiltroFunnelStato(b.key)}
              data-testid={`filter-funnel-stato-${b.key}`}
              className={active ? b.activeCls : b.idleCls}
            >
              {b.label}
              <span className={`ml-2 px-1.5 py-0.5 rounded-full text-xs ${active ? b.badgeActive : b.badgeIdle}`}>
                {counts[b.key]}
              </span>
            </Button>
          );
        })}
      </div>
    );
  };

  // Componente riga tab Follow UP con storico chiamate e memo
  const FollowUpRow = ({ cliente }) => {
    const [addingCall, setAddingCall] = useState(false);
    const [newCallNote, setNewCallNote] = useState("");
    const [editingIdx, setEditingIdx] = useState(null);
    const [viewingIdx, setViewingIdx] = useState(null);
    const scaduto = memoScaduto(cliente.memo_datetime);
    const giorni = giorniDaConsegnato(cliente.data_consegnato);
    const chiamate = Array.isArray(cliente.chiamate_log) ? cliente.chiamate_log : [];
    const cellCls = "border-b border-slate-200 p-2 align-top text-sm";
    const inputCls = "text-xs w-full min-h-[32px] px-2 py-1 rounded border border-slate-200 focus:border-blue-400 focus:outline-none";

    const submitNewCall = () => {
      const nota = newCallNote.trim();
      if (!nota) {
        setAddingCall(false);
        setNewCallNote("");
        return;
      }
      handleAddChiamata(cliente.id, nota);
      setNewCallNote("");
      setAddingCall(false);
    };

    return (
      <tr className={scaduto && !cliente.memo_muted ? "bg-rose-100 hover:bg-rose-200 animate-pulse" : "hover:bg-slate-50"} data-testid={`follow-up-row-${cliente.id}`}>
        <td className={cellCls}>
          <Link
            to={`/interviews?search=${encodeURIComponent(`${cliente.nome || ""} ${cliente.cognome || ""}`.trim())}`}
            className="font-medium text-slate-900 hover:text-blue-600 hover:underline inline-flex items-center gap-1"
          >
            {cliente.nome} {cliente.cognome}
            <ExternalLink className="w-3 h-3" />
          </Link>
        </td>
        <td className={cellCls}>
          <a href={`tel:${cliente.telefono}`} className="text-blue-600 hover:underline inline-flex items-center gap-1">
            <Phone className="w-3 h-3" /> {cliente.telefono || "—"}
          </a>
        </td>
        <td className={cellCls + " text-center"}>
          <span className={`inline-flex items-center gap-1 font-semibold ${giorni !== "—" && giorni > 5 ? "text-red-600" : "text-slate-700"}`}>
            <Clock className="w-3 h-3" /> {giorni}
          </span>
        </td>
        <td className={cellCls + " min-w-[280px] max-w-[420px]"}>
          <div className="flex flex-col gap-1">
            {chiamate.length === 0 && !addingCall && (
              <span className="text-xs text-slate-400 italic">Nessuna chiamata registrata</span>
            )}
            {chiamate.map((c, idx) => {
              const isEditing = editingIdx === idx;
              const isViewing = viewingIdx === idx;
              if (isEditing) {
                return (
                  <div key={idx} className="flex flex-col gap-1 bg-blue-50 border border-blue-200 rounded p-2">
                    <span className="text-[10px] text-slate-500 font-medium">{formatChiamataDate(c.data)}</span>
                    <textarea
                      className={inputCls + " min-h-[70px] resize-y"}
                      defaultValue={c.nota || ""}
                      autoFocus
                      data-testid={`chiamata-edit-${cliente.id}-${idx}`}
                      onBlur={(e) => {
                        const v = e.target.value.trim();
                        setEditingIdx(null);
                        if (v !== (c.nota || "")) handleUpdateChiamataNota(cliente.id, idx, v);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Escape") setEditingIdx(null);
                      }}
                    />
                  </div>
                );
              }
              return (
                <div key={idx} className="flex items-start gap-1 group">
                  <button
                    type="button"
                    onClick={() => setViewingIdx(isViewing ? null : idx)}
                    data-testid={`chiamata-toggle-${cliente.id}-${idx}`}
                    className="bg-transparent flex-1 text-left text-xs bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded px-2 py-1"
                    style={{ backgroundColor: "#f8fafc", color: "#334155" }}
                    title="Clicca per espandere/comprimere"
                  >
                    <div className="font-medium text-slate-500 text-[10px]">{formatChiamataDate(c.data)}</div>
                    {isViewing ? (
                      <div className="text-slate-700 whitespace-pre-wrap break-words mt-0.5">{c.nota || "(vuota)"}</div>
                    ) : (
                      <div className="text-slate-700 line-clamp-1">{c.nota || "(vuota)"}</div>
                    )}
                  </button>
                  <div className="flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      type="button"
                      onClick={() => setEditingIdx(idx)}
                      className="bg-transparent text-blue-600 hover:text-blue-800 text-[10px] px-1"
                      style={{ backgroundColor: "transparent" }}
                      data-testid={`chiamata-edit-btn-${cliente.id}-${idx}`}
                      title="Modifica nota"
                    >
                      ✎
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteChiamata(cliente.id, idx)}
                      className="bg-transparent text-rose-500 hover:text-rose-700 text-[10px] px-1"
                      style={{ backgroundColor: "transparent" }}
                      data-testid={`chiamata-del-${cliente.id}-${idx}`}
                      title="Elimina chiamata"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              );
            })}
            {addingCall ? (
              <div className="flex flex-col gap-1 bg-emerald-50 border border-emerald-200 rounded p-2">
                <span className="text-[10px] text-emerald-700 font-medium">Nuova chiamata — {formatChiamataDate(new Date().toISOString())}</span>
                <textarea
                  className={inputCls + " min-h-[70px] resize-y"}
                  placeholder="Descrivi cosa è successo in chiamata…"
                  value={newCallNote}
                  onChange={(e) => setNewCallNote(e.target.value)}
                  autoFocus
                  data-testid={`chiamata-new-textarea-${cliente.id}`}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      setAddingCall(false);
                      setNewCallNote("");
                    }
                  }}
                />
                <div className="flex gap-1 justify-end">
                  <button
                    type="button"
                    onClick={() => { setAddingCall(false); setNewCallNote(""); }}
                    className="bg-transparent text-[11px] px-2 py-0.5 text-slate-600 hover:text-slate-800"
                    style={{ backgroundColor: "transparent" }}
                    data-testid={`chiamata-new-cancel-${cliente.id}`}
                  >
                    Annulla
                  </button>
                  <button
                    type="button"
                    onClick={submitNewCall}
                    className="text-[11px] px-2 py-0.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-medium"
                    style={{ backgroundColor: "#059669", color: "#ffffff" }}
                    data-testid={`chiamata-new-save-${cliente.id}`}
                  >
                    Salva
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-1 mt-1">
                <button
                  type="button"
                  onClick={() => setAddingCall(true)}
                  data-testid={`chiamata-add-${cliente.id}`}
                  className="bg-transparent inline-flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-900 border border-dashed border-emerald-300 hover:border-emerald-500 rounded px-2 py-1 self-start"
                  style={{ backgroundColor: "#ffffff", color: "#047857" }}
                  title="Scrivi una nota personalizzata"
                >
                  <PhoneCall className="w-3 h-3" />
                  Nuova
                </button>
                {[
                  { label: "Non risponde", nota: "Non risponde", bg: "#fef2f2", color: "#b91c1c", border: "#fecaca" },
                  { label: "Occupato", nota: "Occupato", bg: "#fffbeb", color: "#b45309", border: "#fde68a" },
                  { label: "Richiamare", nota: "Richiamare", bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" },
                ].map((preset) => (
                  <button
                    key={preset.label}
                    type="button"
                    onClick={() => handleAddChiamata(cliente.id, preset.nota)}
                    data-testid={`chiamata-preset-${preset.label.toLowerCase().replace(/\s/g, "-")}-${cliente.id}`}
                    className="bg-transparent text-[10px] px-2 py-1 rounded border font-medium"
                    style={{ backgroundColor: preset.bg, color: preset.color, borderColor: preset.border }}
                    title={`Registra chiamata: ${preset.nota}`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </td>
        <td className={cellCls + " min-w-[280px]"}>
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1">
              <input
                type="datetime-local"
                className={inputCls + (scaduto ? " border-rose-400 bg-rose-100 text-rose-700 font-medium" : "")}
                defaultValue={isoToLocalInput(cliente.memo_datetime)}
                data-testid={`fu-memo-${cliente.id}`}
                onBlur={(e) => {
                  const local = e.target.value;
                  const iso = local ? new Date(local).toISOString() : "";
                  if (iso !== (cliente.memo_datetime || "")) handleUpdateFollowUp(cliente.id, "memo_datetime", iso);
                }}
              />
              {cliente.memo_datetime && (
                <button
                  type="button"
                  onClick={() => handleUpdateFollowUp(cliente.id, "memo_muted", !cliente.memo_muted)}
                  className="bg-transparent p-1 flex-shrink-0"
                  style={{ backgroundColor: "transparent" }}
                  data-testid={`fu-memo-mute-${cliente.id}`}
                  title={cliente.memo_muted ? "Riattiva allarme sonoro" : "Silenzia allarme sonoro"}
                >
                  {cliente.memo_muted ? (
                    <BellOff className="w-4 h-4 text-slate-400 hover:text-slate-600" />
                  ) : (
                    <Bell className={`w-4 h-4 ${scaduto ? "text-rose-600 hover:text-rose-800" : "text-slate-500 hover:text-slate-700"}`} />
                  )}
                </button>
              )}
              {cliente.memo_datetime && (
                <button
                  type="button"
                  onClick={async () => {
                    if (!window.confirm(`Eliminare il memo di ${cliente.nome} ${cliente.cognome}?`)) return;
                    // Cancella data + nota + mute in un colpo solo
                    try {
                      await axios.put(`${API}/interviews/${cliente.id}`, {
                        memo_datetime: "",
                        memo_nota: "",
                        memo_muted: false,
                      }, { withCredentials: true });
                      setInterviews((prev) => prev.map((i) => i.id === cliente.id
                        ? { ...i, memo_datetime: "", memo_nota: "", memo_muted: false }
                        : i));
                      toast.success("Memo eliminato");
                    } catch (err) {
                      const msg = err?.response?.data?.detail || err.message || "Errore";
                      toast.error(`Errore: ${msg}`);
                    }
                  }}
                  className="bg-transparent p-1 flex-shrink-0"
                  style={{ backgroundColor: "transparent" }}
                  data-testid={`fu-memo-clear-${cliente.id}`}
                  title="Elimina memo"
                >
                  <Trash2 className="w-4 h-4 text-rose-500 hover:text-rose-700" />
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-1">
              <button
                type="button"
                onClick={() => {
                  const base = cliente.memo_datetime ? new Date(cliente.memo_datetime) : new Date();
                  const target = base.getTime() < Date.now() ? new Date() : base;
                  target.setMinutes(target.getMinutes() + 30);
                  handleUpdateFollowUp(cliente.id, "memo_datetime", target.toISOString());
                  if (cliente.memo_muted) handleUpdateFollowUp(cliente.id, "memo_muted", false);
                }}
                className="bg-transparent text-[10px] px-2 py-0.5 rounded border border-sky-200 hover:bg-sky-50 text-sky-700 font-medium"
                style={{ backgroundColor: "#f0f9ff", color: "#0369a1" }}
                data-testid={`fu-snooze-30m-${cliente.id}`}
                title="Rimanda di 30 minuti"
              >
                +30m
              </button>
              <button
                type="button"
                onClick={() => {
                  const base = cliente.memo_datetime ? new Date(cliente.memo_datetime) : new Date();
                  const target = base.getTime() < Date.now() ? new Date() : base;
                  target.setHours(target.getHours() + 1);
                  handleUpdateFollowUp(cliente.id, "memo_datetime", target.toISOString());
                  if (cliente.memo_muted) handleUpdateFollowUp(cliente.id, "memo_muted", false);
                }}
                className="bg-transparent text-[10px] px-2 py-0.5 rounded border border-blue-200 hover:bg-blue-50 text-blue-700 font-medium"
                style={{ backgroundColor: "#eff6ff", color: "#1d4ed8" }}
                data-testid={`fu-snooze-1h-${cliente.id}`}
                title="Rimanda di 1 ora"
              >
                +1h
              </button>
              <button
                type="button"
                onClick={() => {
                  const base = cliente.memo_datetime ? new Date(cliente.memo_datetime) : new Date();
                  const target = base.getTime() < Date.now() ? new Date() : base;
                  target.setDate(target.getDate() + 1);
                  handleUpdateFollowUp(cliente.id, "memo_datetime", target.toISOString());
                  if (cliente.memo_muted) handleUpdateFollowUp(cliente.id, "memo_muted", false);
                }}
                className="bg-transparent text-[10px] px-2 py-0.5 rounded border border-indigo-200 hover:bg-indigo-50 text-indigo-700 font-medium"
                style={{ backgroundColor: "#eef2ff", color: "#4338ca" }}
                data-testid={`fu-snooze-1g-${cliente.id}`}
                title="Rimanda di 1 giorno"
              >
                +1g
              </button>
              <button
                type="button"
                onClick={() => {
                  const base = cliente.memo_datetime ? new Date(cliente.memo_datetime) : new Date();
                  const target = base.getTime() < Date.now() ? new Date() : base;
                  target.setDate(target.getDate() + 3);
                  handleUpdateFollowUp(cliente.id, "memo_datetime", target.toISOString());
                  if (cliente.memo_muted) handleUpdateFollowUp(cliente.id, "memo_muted", false);
                }}
                className="bg-transparent text-[10px] px-2 py-0.5 rounded border border-violet-200 hover:bg-violet-50 text-violet-700 font-medium"
                style={{ backgroundColor: "#f5f3ff", color: "#6d28d9" }}
                data-testid={`fu-snooze-3g-${cliente.id}`}
                title="Rimanda di 3 giorni (cliente in ferie)"
              >
                +3g
              </button>
            </div>
          </div>
        </td>
        <td className={cellCls + " min-w-[200px] max-w-[280px]"}>
          <textarea
            className={inputCls + " min-h-[38px] resize-y"}
            placeholder="Motivo del memo…"
            defaultValue={cliente.memo_nota || ""}
            data-testid={`fu-memo-nota-${cliente.id}`}
            onBlur={(e) => {
              const v = e.target.value.trim();
              if (v !== (cliente.memo_nota || "")) handleUpdateFollowUp(cliente.id, "memo_nota", v);
            }}
          />
        </td>
      </tr>
    );
  };

  const ClienteRow = ({ cliente }) => {
    const funnel1Sent = cliente.funnel_brevo?.funnel_1?.sent_at;
    return (
    <div className="flex items-center justify-between p-3 bg-white border rounded-lg hover:bg-slate-50 transition-colors" data-testid={`cliente-row-${cliente.id}`}>
      <div className="flex items-center gap-3">
        <div>
          <Link 
            to={`/interviews?edit=${cliente.id}`}
            className="font-medium text-slate-900 hover:text-blue-600 hover:underline flex items-center gap-1"
          >
            {cliente.nome} {cliente.cognome}
            <ExternalLink className="w-3 h-3" />
          </Link>
          <p className="text-xs text-slate-500">
            {cliente.categoria_merceologica || "N/D"} • {cliente.stato}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => handleAddToFunnel(cliente.id, 1, `${cliente.nome} ${cliente.cognome}`)}
          data-testid={`btn-funnel-1-${cliente.id}`}
          title={funnel1Sent ? `Inviato il ${formatSentDate(funnel1Sent)}. Clicca per rispedire.` : "Sposta su Brevo lista 'Contatti in Ripresa'"}
          className={
            funnel1Sent
              ? "text-xs border-emerald-500 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
              : "text-xs border-blue-300 text-blue-600 hover:bg-blue-50"
          }
        >
          {funnel1Sent ? (
            <>
              <CheckCircle2 className="w-3 h-3 mr-1" />
              Funnel 1 · {formatSentDate(funnel1Sent)}
            </>
          ) : (
            <>
              <Send className="w-3 h-3 mr-1" />
              Funnel 1
            </>
          )}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => handleAddToFunnel(cliente.id, 2, `${cliente.nome} ${cliente.cognome}`)}
          className="text-xs border-green-300 text-green-600 hover:bg-green-50"
        >
          <Send className="w-3 h-3 mr-1" />
          Funnel 2
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => handleAddToFunnel(cliente.id, 3, `${cliente.nome} ${cliente.cognome}`)}
          className="text-xs border-orange-300 text-orange-600 hover:bg-orange-50"
        >
          <Send className="w-3 h-3 mr-1" />
          Funnel 3
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => handleAddToFunnel(cliente.id, 4, `${cliente.nome} ${cliente.cognome}`)}
          className="text-xs border-purple-300 text-purple-600 hover:bg-purple-50"
        >
          <Send className="w-3 h-3 mr-1" />
          Funnel 4
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => handleDeleteCliente(cliente)}
          data-testid={`btn-delete-cliente-${cliente.id}`}
          title="Elimina totalmente il contatto e tutti i suoi dati"
          className="text-xs border-red-300 text-red-600 hover:bg-red-50"
        >
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
    </div>
    );
  };

  if (loading) {
    return (
      <PageLayout user={user} logout={logout} testId="funnel-ripresa-page">
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout user={user} logout={logout} testId="funnel-ripresa-page">
      <div className="p-4 md:p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Follow UP</h1>
            <p className="text-slate-600 text-sm">Gestione richiami clienti e recupero contatti</p>
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex gap-2 border-b border-slate-200">
          {[
            { key: "follow_up", label: "Follow UP", icon: PhoneCall, count: clientiFollowUp.length, color: "text-blue-600" },
            { key: "inconcludenti", label: "Inconcludenti", icon: UserX, count: clientiInconcludenti.length, color: "text-red-500" },
            { key: "fuori_budget", label: "Fuori Budget", icon: Wallet, count: clientiFuoriBudget.length, color: "text-amber-500" },
          ].map((t) => {
            const Icon = t.icon;
            const active = activeTab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setActiveTab(t.key)}
                data-testid={`tab-${t.key}`}
                className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px flex items-center gap-2 ${
                  active
                    ? "border-[#1e40af] text-[#1e40af]"
                    : "border-transparent text-slate-500 hover:text-slate-700"
                }`}
              >
                <Icon className={`w-4 h-4 ${active ? "" : t.color}`} />
                {t.label}
                <span className="ml-1 text-xs bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">
                  {t.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Tab: Follow UP */}
        {activeTab === "follow_up" && (
          <Card data-testid="follow-up-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <PhoneCall className="w-5 h-5 text-blue-600" />
                Clienti con Offerta Consegnata
                <span className="ml-2 px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                  {clientiFollowUp.length}
                </span>
                {/* Banner permesso notifiche */}
                {notifPermission === "default" && (
                  <button
                    type="button"
                    onClick={requestNotifPermission}
                    data-testid="btn-enable-notifications"
                    className="ml-auto inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 font-medium"
                    style={{ backgroundColor: "#fffbeb", color: "#92400e" }}
                  >
                    <Bell className="w-3 h-3" />
                    Attiva notifiche desktop
                  </button>
                )}
                {notifPermission === "granted" && (
                  <span
                    className="ml-auto inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-medium"
                    data-testid="badge-notifications-active"
                    title="Riceverai notifiche desktop anche da altre tab"
                  >
                    <Bell className="w-3 h-3" />
                    Notifiche attive
                  </span>
                )}
                {notifPermission === "denied" && (
                  <span
                    className="ml-auto inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 font-medium"
                    title="Le notifiche sono state bloccate. Abilitarle dalle impostazioni del browser."
                  >
                    <BellOff className="w-3 h-3" />
                    Notifiche bloccate
                  </span>
                )}
              </CardTitle>
              <p className="text-sm text-slate-500">
                Segui i richiami e imposta un memo per non dimenticare azioni importanti. Le righe in rosso hanno un memo scaduto.
              </p>
            </CardHeader>
            <CardContent>
              {/* Barra filtri Follow UP: ricerca + filtri stato memo + ordinamento */}
              <div className="flex flex-col sm:flex-row gap-2 mb-4">
                <div className="relative flex-1 max-w-md">
                  <input
                    type="text"
                    placeholder="Cerca per nome, cognome o telefono…"
                    value={followUpSearch}
                    onChange={(e) => setFollowUpSearch(e.target.value)}
                    data-testid="fu-search"
                    className="w-full h-9 px-3 rounded border border-slate-200 text-sm focus:border-blue-400 focus:outline-none"
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  {[
                    { key: "all", label: "Tutti", color: "slate" },
                    { key: "scaduto", label: "Scaduti", color: "rose" },
                    { key: "oggi", label: "Oggi", color: "amber" },
                    { key: "futuro", label: "Futuri", color: "emerald" },
                    { key: "senza", label: "Senza memo", color: "gray" },
                  ].map((f) => {
                    const active = followUpMemoFilter === f.key;
                    const bg = active
                      ? { slate: "#334155", rose: "#e11d48", amber: "#f59e0b", emerald: "#059669", gray: "#64748b" }[f.color]
                      : "#ffffff";
                    const color = active ? "#ffffff" : "#334155";
                    return (
                      <button
                        key={f.key}
                        type="button"
                        onClick={() => setFollowUpMemoFilter(f.key)}
                        data-testid={`fu-filter-${f.key}`}
                        className="bg-transparent text-xs px-3 py-1 rounded-full border border-slate-200 font-medium"
                        style={{ backgroundColor: bg, color, borderColor: active ? bg : "#e2e8f0" }}
                      >
                        {f.label}
                        <span className="ml-1 opacity-75">({followUpCounts[f.key]})</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              {/* Ordinamento + Stampa */}
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="text-xs text-slate-500 font-medium">Ordina per:</span>
                {[
                  { key: "memo_asc", label: "Data Memo ↑", title: "Dal più vicino al più lontano" },
                  { key: "memo_desc", label: "Data Memo ↓", title: "Dal più lontano al più vicino" },
                  { key: "giorni_desc", label: "Giorni ↓", title: "Consegnati da più tempo" },
                  { key: "giorni_asc", label: "Giorni ↑", title: "Consegnati più recentemente" },
                ].map((s) => {
                  const active = followUpSort === s.key;
                  return (
                    <button
                      key={s.key}
                      type="button"
                      onClick={() => setFollowUpSort(s.key)}
                      data-testid={`fu-sort-${s.key}`}
                      title={s.title}
                      className="bg-transparent text-xs px-3 py-1 rounded border font-medium"
                      style={{
                        backgroundColor: active ? "#1e40af" : "#ffffff",
                        color: active ? "#ffffff" : "#1e40af",
                        borderColor: active ? "#1e40af" : "#bfdbfe",
                      }}
                    >
                      {s.label}
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={handlePrintFollowUp}
                  data-testid="fu-print"
                  title="Genera una lista stampabile dei clienti filtrati"
                  className="bg-transparent ml-auto text-xs px-3 py-1 rounded border font-medium inline-flex items-center gap-1"
                  style={{ backgroundColor: "#059669", color: "#ffffff", borderColor: "#059669" }}
                >
                  <Printer className="w-3 h-3" />
                  Stampa lista giornata
                </button>
              </div>
              {clientiFollowUpFiltered.length === 0 ? (
                <p className="text-slate-500 text-center py-8">
                  {clientiFollowUp.length === 0 ? "Nessun cliente con offerta consegnata" : "Nessun cliente corrisponde ai filtri"}
                </p>
              ) : (
                <div className="overflow-x-auto border border-slate-200 rounded-lg">
                  <table className="w-full min-w-[1000px] text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-2 text-xs font-semibold text-slate-600 uppercase tracking-wide">Nominativo</th>
                        <th className="text-left p-2 text-xs font-semibold text-slate-600 uppercase tracking-wide">Telefono</th>
                        <th className="text-center p-2 text-xs font-semibold text-slate-600 uppercase tracking-wide">Giorni</th>
                        <th className="text-left p-2 text-xs font-semibold text-slate-600 uppercase tracking-wide">Chiamate</th>
                        <th className="text-left p-2 text-xs font-semibold text-slate-600 uppercase tracking-wide">Data Memo</th>
                        <th className="text-left p-2 text-xs font-semibold text-slate-600 uppercase tracking-wide">Nota Memo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clientiFollowUpFiltered.map((cliente) => (
                        <FollowUpRow key={cliente.id} cliente={cliente} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Tab: Inconcludenti */}
        {activeTab === "inconcludenti" && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <UserX className="w-5 h-5 text-red-500" />
                Inconcludenti
                <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                  {applyFunnelFilter(inconcludentiFiltrati).length}
                </span>
              </CardTitle>
              <p className="text-sm text-slate-500">
                Clienti con stato: Non Risposto, In Attesa Riscontri, Sparito, Perditempo
              </p>
            </CardHeader>
            <CardContent>
              <FiltriMarketing 
                filtro={filtroInconcludenti} 
                setFiltro={setFiltroInconcludenti} 
                counts={countInconcludenti}
              />
              <FiltriStatoFunnel lista={inconcludentiFiltrati} />
              
              {applyFunnelFilter(inconcludentiFiltrati).length === 0 ? (
                <p className="text-slate-500 text-center py-8">Nessun cliente trovato con questi filtri</p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {applyFunnelFilter(inconcludentiFiltrati).map(cliente => (
                    <ClienteRow key={cliente.id} cliente={cliente} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Tab: Fuori Budget */}
        {activeTab === "fuori_budget" && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Wallet className="w-5 h-5 text-amber-500" />
                Fuori Budget
                <span className="ml-2 px-2 py-0.5 bg-amber-500 text-white text-xs rounded-full">
                  {applyFunnelFilter(fuoriBudgetFiltrati).length}
                </span>
              </CardTitle>
              <p className="text-sm text-slate-500">
                Clienti che hanno indicato budget insufficiente
              </p>
            </CardHeader>
            <CardContent>
              <FiltriMarketing 
                filtro={filtroFuoriBudget} 
                setFiltro={setFiltroFuoriBudget} 
                counts={countFuoriBudget}
              />
              <FiltriStatoFunnel lista={fuoriBudgetFiltrati} />
              
              {applyFunnelFilter(fuoriBudgetFiltrati).length === 0 ? (
                <p className="text-slate-500 text-center py-8">Nessun cliente trovato con questi filtri</p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {applyFunnelFilter(fuoriBudgetFiltrati).map(cliente => (
                    <ClienteRow key={cliente.id} cliente={cliente} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </PageLayout>
  );
}
